/**
 * Talent Acquisition — hooks tRPC (acceso, dashboard, governance).
 * `checkAccess` nunca tira: sirve de gate para la ruta y el sidebar.
 */
import type { TaAgentVariant } from '@/contexts/PageContext';
import { useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import type { RouterInputs } from '@/lib/api-types';
import { toast } from 'sonner';
import { toastError } from '@/lib/toast-error';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@/contexts/PageContext';

/**
 * La página de Talent Acquisition lo llama al montar: supportIA (burbuja del
 * FAB + drawer) pasa a modo Agente TA Metrics — mismo mecanismo que el Hub de
 * Partnerships con `partnerCtx`. Al salir vuelve al modo general.
 */
export function useRegisterTaContext(variant: TaAgentVariant = 'metrics', enabled = true) {
  const { setTaCtx } = usePageContext();
  useEffect(() => {
    // `enabled=false`: quien no es de TA no tiene por qué ver el bot sobre la base entera.
    if (!enabled) return;
    setTaCtx({ section: 'talent-acquisition', variant });
    return () => setTaCtx(null);
  }, [setTaCtx, variant, enabled]);
}

export function useTaAccess() {
  return trpc.taMetrics.checkAccess.useQuery(undefined, { staleTime: 60_000, retry: false });
}

/** `enabled=false` para quien entra solo como Hiring Manager (canIntake sin canView): el dashboard le daría FORBIDDEN. */
export function useTaDashboard(refresh = false, enabled = true) {
  return trpc.taMetrics.getDashboard.useQuery({ refresh }, { staleTime: 5 * 60_000, retry: 1, enabled });
}

export function useTaGovernance() {
  return trpc.taMetrics.governance.getGovernance.useQuery(undefined, { retry: false });
}

export function useTaCommunityPicker() {
  return trpc.taMetrics.governance.listCommunityForPicker.useQuery(undefined, { staleTime: 5 * 60_000, retry: false });
}

export function useSaveTaGovernance() {
  const utils = trpc.useUtils();
  return trpc.taMetrics.governance.setGovernance.useMutation({
    onSuccess: () => {
      utils.taMetrics.governance.getGovernance.invalidate();
      utils.taMetrics.checkAccess.invalidate();
      utils.profile.getVisibility.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
}

/** Admin de TA (28/09): Hiring Managers — quién puede abrir una búsqueda. Solo admins de la sección. */
export function useTaIntakeGovernance() {
  return trpc.taMetrics.governance.getIntakeGovernance.useQuery(undefined, { retry: false });
}

export function useSaveTaIntakeGovernance() {
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  return trpc.taMetrics.governance.setIntakeGovernance.useMutation({
    onSuccess: () => {
      toast.success(t('talent_acquisition.hiring_managers.saved'));
      utils.taMetrics.governance.getIntakeGovernance.invalidate();
      utils.taMetrics.checkAccess.invalidate();
      utils.profile.getVisibility.invalidate();
    },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar_configuracion'),
  });
}

/** Admin de TA (28/09): system prompts de los agentes (efectivo + qué está sobreescrito). */
export function useTaAgentPrompts() {
  return trpc.taMetrics.governance.getAgentPrompts.useQuery(undefined, { retry: false, staleTime: 60_000 });
}

export function useSaveTaAgentPrompts() {
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  return trpc.taMetrics.governance.setAgentPrompts.useMutation({
    onSuccess: (view) => {
      // La mutación devuelve la vista nueva: no hace falta refetch
      utils.taMetrics.governance.getAgentPrompts.setData(undefined, view);
      toast.success(t('talent_acquisition.agent_prompts.saved'));
    },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar_configuracion'),
  });
}

/** RNF-08: reglas de cálculo (ETAPAS_ABIERTAS / MAPEO_OWNERS). Lectura para cualquiera con acceso. */
export function useTaRules() {
  return trpc.taMetrics.governance.getRules.useQuery(undefined, { retry: false });
}

export function useSaveTaRules() {
  const utils = trpc.useUtils();
  return trpc.taMetrics.governance.setRules.useMutation({
    onSuccess: () => {
      utils.taMetrics.governance.getRules.invalidate();
      // El dashboard se recalcula con las reglas nuevas
      utils.taMetrics.getDashboard.invalidate();
    },
    onError: (e) => {
      toast.error(e.message);
      // CONFLICT: alguien guardó en el medio → se recarga la versión actual
      if (e.data?.code === 'CONFLICT') utils.taMetrics.governance.getRules.invalidate();
    },
  });
}

/** V1.3 §15: filas normalizadas de la Base agente para la vista Hiring Plan (misma normalización que el bot, RD-07). */
export function useTaHiringPlanRows() {
  return trpc.taMetrics.listRows.useQuery(undefined, { staleTime: 5 * 60_000, retry: 1 });
}

/** RD-09: qué cambió desde una foto anterior para el filtro activo de la vista. */
export function useCompareTaSnapshots(input: RouterInputs['taMetrics']['snapshots']['compare'], enabled: boolean) {
  return trpc.taMetrics.snapshots.compare.useQuery(input, { enabled, staleTime: 60_000, retry: false });
}

/** V1.3 §18 (RD-08): PDF de status de una búsqueda; abre la URL prefirmada en otra pestaña (mismo patrón que las facturas de Kits). */
export function useTaReporteBusqueda() {
  const { t } = useTranslation();
  return trpc.taMetrics.reporteBusqueda.useMutation({
    onSuccess: (r) => {
      window.open(r.url, '_blank', 'noopener');
      toast.success(t('talent_acquisition.hiring_plan.detail.pdf_ok', { archivo: r.archivo }));
    },
    onError: (e) => toastError(e, 'common.toast.no_se_pudo_generar_el_pdf'),
  });
}

/** V1.3 §14: fotos semanales de la Hiring Plan (histórico de estados). Lectura para cualquiera con acceso. */
export function useTaSnapshots() {
  return trpc.taMetrics.snapshots.list.useQuery(undefined, { retry: false });
}

/** Tomar una foto a mano (admin de la sección). El cron de los lunes la toma sola en producción. */
export function useTakeTaSnapshot() {
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  return trpc.taMetrics.snapshots.take.useMutation({
    onSuccess: (info) => {
      toast.success(t('talent_acquisition.snapshots.taken', { fecha: info.fecha, busquedas: info.busquedas }));
      utils.taMetrics.snapshots.list.invalidate();
    },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar'),
  });
}

/** Actividad del agente (audit): páginas de 50, la más reciente primero. Solo admins de la sección. */
export function useTaActivity() {
  return trpc.taMetrics.governance.listActivity.useInfiniteQuery(
    { limit: 50 },
    { getNextPageParam: (last) => last.cursor ?? undefined, initialCursor: null, retry: false, staleTime: 30_000 },
  );
}

// ─── Hiring Manager Intake (25/09/2026) ──────────────────────────────────────

/** Mis búsquedas (HM): en curso y guardadas. */
export function useTaIntakeMine(enabled = true) {
  return trpc.taIntake.listMine.useQuery(undefined, { enabled, staleTime: 30_000, retry: false });
}

/** Una búsqueda propia (transcripción + versiones de la JD; nunca la guía). */
export function useTaIntakeSession(sessionId: string | null) {
  return trpc.taIntake.get.useQuery({ sessionId: sessionId ?? '' }, { enabled: !!sessionId, staleTime: 15_000, retry: false });
}

export function useStartTaIntake() {
  const utils = trpc.useUtils();
  return trpc.taIntake.start.useMutation({
    onSuccess: (view) => {
      utils.taIntake.get.setData({ sessionId: view.session.sessionId }, view);
      utils.taIntake.listMine.invalidate();
    },
    onError: (e) => toastError(e, 'common.toast.error_al_crear'),
  });
}

/** Wrapper tRPC del turno (compat): el chat usa la Function URL cuando está configurada. */
export function useTaIntakeTurnTrpc() {
  return trpc.taIntake.turn.useMutation();
}

export function useArchiveTaIntake() {
  const utils = trpc.useUtils();
  return trpc.taIntake.archive.useMutation({
    onSuccess: () => { utils.taIntake.listMine.invalidate(); utils.taIntake.list.invalidate(); },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar'),
  });
}

/** PDF de la JD aprobada (HM o TA) o de la Sourcing Guide (TA): abre la URL prefirmada (mismo patrón que el PDF de status). */
export function useTaIntakePdf() {
  return trpc.taIntake.exportPdf.useMutation({
    onSuccess: (r) => { window.open(r.url, '_blank', 'noopener'); },
    onError: (e) => toastError(e, 'common.toast.no_se_pudo_generar_el_pdf'),
  });
}

/** Repositorio de búsquedas (TA). */
export function useTaIntakeList(enabled = true) {
  return trpc.taIntake.list.useQuery(undefined, { enabled, staleTime: 30_000, retry: false });
}

export function useTaIntakeDetail(sessionId: string | null) {
  return trpc.taIntake.detail.useQuery({ sessionId: sessionId ?? '' }, { enabled: !!sessionId, staleTime: 15_000, retry: false });
}

export function useSetTaIntakeStatus() {
  const utils = trpc.useUtils();
  return trpc.taIntake.setStatus.useMutation({
    onSuccess: (s) => {
      utils.taIntake.list.invalidate();
      utils.taIntake.detail.invalidate({ sessionId: s.sessionId });
    },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar'),
  });
}
